/** Choose tours: how the discovery rows are grouped, ordered and worded. Pure, so the ranking is
 *  unit-tested apart from the DOM. (The console's onboard/products.ts is the reference; the client
 *  sees fewer fields.) */
import { CADENCES } from './labels';
import type { OnboardingProduct } from './types';

/** Row states the discovery pass writes once a product has been checked. */
const CHECKED = new Set(['done', 'no_bookings', 'error']);

export const isChecked = (p: OnboardingProduct) => CHECKED.has(p.state);

export interface ProductGroups {
    /** Checked rows with bookings (most bookings first), then failed rows, then rows still being
     *  checked, in catalogue order. */
    main: OnboardingProduct[];
    /** Checked rows with nothing to import: the collapsed list. */
    empty: OnboardingProduct[];
}

export function groupProducts(products: OnboardingProduct[]): ProductGroups {
    const found: OnboardingProduct[] = [];
    const failed: OnboardingProduct[] = [];
    const checking: OnboardingProduct[] = [];
    const empty: OnboardingProduct[] = [];
    products.forEach((p) => {
        if (!isChecked(p)) {
            checking.push(p);
        } else if (p.state === 'error') {
            failed.push(p);
        } else if (p.bookings_found === 0) {
            empty.push(p);
        } else {
            found.push(p);
        }
    });
    // A stable sort: equal counts keep catalogue order.
    found.sort((a, b) => b.bookings_found - a.bookings_found);
    return { main: [...found, ...failed, ...checking], empty };
}

/** "8+" when the per-product sample stopped counting. */
export const bookingsLabel = (p: OnboardingProduct) => (p.capped ? `${p.bookings_found}+` : String(p.bookings_found));

export function plural(n: number, one: string, many = `${one}s`): string {
    return `${n} ${n === 1 ? one : many}`;
}

/** "45 min", "2 h", "2 h 30 min", "3 days". */
export function durationLabel(minutes: number | null | undefined): string | null {
    if (minutes == null || minutes <= 0) {
        return null;
    }
    if (minutes >= 24 * 60) {
        const days = Math.round(minutes / (24 * 60));
        return plural(days, 'day');
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (!h) {
        return `${m} min`;
    }
    return m ? `${h} h ${m} min` : `${h} h`;
}

/** "Day tour · 2 h" / "Multi-day · 3 days", or whichever half is known, or null. */
export function rhythmLabel(p: Pick<OnboardingProduct, 'cadence' | 'duration_min'>): string | null {
    const kind = (p.cadence && CADENCES[p.cadence]) || null;
    const duration = durationLabel(p.duration_min);
    if (kind && duration) {
        return `${kind} · ${duration}`;
    }
    return kind || duration;
}

/** "Sat 11 Oct, 10:00": the departure's own wall clock. The booking system sends its local time
 *  with its offset ("2026-10-13T10:00:00+0200"), so the date and time are read from the string
 *  itself, never converted to the browser's zone. */
export function departureLabel(iso: string | null | undefined): string | null {
    if (!iso) {
        return null;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
    if (!m) {
        return null;
    }
    const wall = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
    if (Number.isNaN(wall.getTime())) {
        return null;
    }
    // Assembled from parts: en-GB puts a comma after the weekday in some ICU versions and not in
    // others (Node 20's "Tue, 13 Oct" vs newer "Tue 13 Oct"), so format() differs by browser.
    const parts = DEPARTURE_FORMAT.formatToParts(wall);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
    return `${part('weekday')} ${part('day')} ${part('month')}, ${part('hour')}:${part('minute')}`;
}

const DEPARTURE_FORMAT = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
});

/** The sample bookings the ticked products bring (their found counts). */
export const sampleBookingCount = (products: OnboardingProduct[], selected: ReadonlySet<number>) =>
    products.reduce((n, p) => (selected.has(p.pk) ? n + p.bookings_found : n), 0);

let regionNames: any;
/** "France" for "FR"; the code itself when the browser cannot name it. */
export function countryName(code: string | null | undefined): string {
    if (!code) {
        return '';
    }
    if (regionNames === undefined) {
        try {
            regionNames = new (Intl as any).DisplayNames(['en'], { type: 'region' });
        } catch (_e) {
            regionNames = null;
        }
    }
    try {
        return regionNames?.of(code.toUpperCase()) ?? code;
    } catch (_e) {
        return code;
    }
}

/** A lane summary as a line of its own: no trailing period. */
export const plainSummary = (text: string | null | undefined) => (text || '').trim().replace(/[.;:,\s]+$/, '');
